import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import maplibregl from "maplibre-gl";
import { MapContext, MapLoadedContext } from "./Map";
import styled from "styled-components";
import { Icon } from "@awsui/components-react";
import theme from "../../../common/theme";

const MapPopupTableContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 1rem;
  margin-bottom: 1rem;
`;
const MapPopupRow = styled.div`
  display: flex;
  flex-direction: row;
`;
const MapPopupColKey = styled.div`
  flex-grow: 0;
  flex-shrink: 0;
  padding-right: 1rem;
  width: 175px;
  font-weight: 500;
  color: #222;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MapPopupColValue = styled.div`
  flex: 1;
  word-break: break-all;
`;

const MapPopupTable = (props: { feature: maplibregl.MapboxGeoJSONFeature }) => {
  const { feature } = props;
  const properties = feature.properties as {
    [key: string]: string | boolean | number | null;
  };
  const propertyKeys = Object.keys(properties).sort();
  return (
    <MapPopupTableContainer>
      {propertyKeys.map((propertyKey, propertyKeyIndex) => (
        <MapPopupRow key={propertyKeyIndex}>
          <MapPopupColKey title={propertyKey}>{propertyKey}</MapPopupColKey>
          <MapPopupColValue>{properties[propertyKey]}</MapPopupColValue>
        </MapPopupRow>
      ))}
    </MapPopupTableContainer>
  );
};
const PopupHeaderContent = styled.div`
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  font-weight: 500;
  font-size: 1.45rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${theme.grey150};
  overflow: auto;
  margin-bottom: 1rem;
  .left {
    display: flex;
    flex-direction: column;
  }

  .right {
    .count {
      font-size: 1.25rem;
      font-weight: 500;
      color: ${theme.textLabel};
      margin-right: 0.5rem;
    }
    display: flex;
    flex-direction: row;
  }
  .title {
    line-height: 1.65rem;
  }
  .subtitle {
    font-size: 1rem;
    line-height: 1rem;
    font-weight: 500;
    color: ${theme.textIconSubtle};
  }
`;

const IconLink = styled.span<{
  disabled?: boolean;
}>`
  ${(p) =>
    !p.disabled
      ? `&:hover {
      color: ${theme.grey600};
      cursor: pointer;
    }`
      : `
      cursor: not-allowed;
      color: ${theme.textDisabled};
      `}
  display:flex;
  align-items: center;
  width: 1.25rem;
  margin-right: 0.25rem;
  svg {
    width: 1.25rem !important;
    height: 1.25rem !important;
  }
`;

const MapPopupContainer = styled.div`
  max-height: 300px;
  display: flex;
  flex-direction: column;
  :focus,
  :active {
    outline: none;
  }
`;
const PopupHeader = (props: {
  title: string;
  subtitle?: string | undefined;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
  featureCount: number;
  featureIndex: number;
}) => {
  const {
    title,
    subtitle,
    onClose,
    onNext,
    onPrev,
    featureCount,
    featureIndex,
  } = props;
  return (
    <PopupHeaderContent>
      <div className="left">
        <div className="title"> {title}</div>
        <div className="subtitle">{subtitle}</div>
      </div>
      <div className="right">
        <div className="count">
          {featureIndex + 1} of {featureCount}
        </div>
        <IconLink onClick={() => onPrev()} disabled={featureIndex === 0}>
          <Icon name="angle-left" size="small" variant="normal" />
        </IconLink>
        <IconLink
          onClick={() => onNext()}
          disabled={featureIndex + 1 === featureCount}
        >
          <Icon name="angle-right" size="small" variant="normal" />
        </IconLink>
        <IconLink onClick={onClose}>
          <Icon name="close" size="small" variant="normal" />
        </IconLink>
      </div>
    </PopupHeaderContent>
  );
};
const PopupBody = styled.div`
  overflow: auto;
`;

const MapPopupContent = (props: {
  onClose: () => void;
  renderTitle?: (feature: maplibregl.MapboxGeoJSONFeature) => string;
  renderSubtitle?: (feature: maplibregl.MapboxGeoJSONFeature) => string;
  features: maplibregl.MapboxGeoJSONFeature[];
}) => {
  const { features, renderTitle, renderSubtitle, onClose } = props;
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);
  const mapPopupContainerRef = useRef<HTMLDivElement>(null);
  const onNext = () => {
    if (currentFeatureIndex + 1 < features.length) {
      setCurrentFeatureIndex(currentFeatureIndex + 1);
    }
  };
  const onPrev = () => {
    if (currentFeatureIndex > 0) {
      setCurrentFeatureIndex(currentFeatureIndex - 1);
    }
  };
  useEffect(() => {
    if (mapPopupContainerRef.current) {
      mapPopupContainerRef.current.focus();
    }
  }, [features]);
  useEffect(() => {
    setCurrentFeatureIndex(0);
  }, [features]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.code === "ArrowRight") {
      e.preventDefault();
      onNext();
    } else if (e.code === "ArrowLeft") {
      e.preventDefault();
      onPrev();
    } else if (e.code === "Escape") {
      e.preventDefault();
      onClose();
    }
  };
  if (features.length === 0) {
    return <>"No features found"</>;
  }
  if (currentFeatureIndex >= features.length) {
    return null;
  }
  const feature = features[currentFeatureIndex];
  let title = feature.layer["source-layer"] || "Feature";
  let subtitle =
    typeof feature.layer.source === "string" ? feature.layer.source : undefined;
  if (renderTitle) {
    title = renderTitle(feature);
  }
  if (renderSubtitle) {
    subtitle = renderSubtitle(feature);
  }
  return (
    <MapPopupContainer
      ref={mapPopupContainerRef}
      tabIndex={0}
      onKeyDown={onKeyDown}
    >
      <PopupHeader
        onClose={onClose}
        title={title}
        subtitle={subtitle}
        featureCount={features.length}
        featureIndex={currentFeatureIndex}
        onNext={onNext}
        onPrev={onPrev}
      />
      <PopupBody>
        <MapPopupTable feature={feature} />
      </PopupBody>
    </MapPopupContainer>
  );
};

const MapPopup = (props: {
  layerNames: string[];
  renderTitle?: (feature: maplibregl.MapboxGeoJSONFeature) => string;
  renderSubtitle?: (feature: maplibregl.MapboxGeoJSONFeature) => string;
}) => {
  const { layerNames, renderTitle, renderSubtitle } = props;
  const map = useContext(MapContext);
  const mapLoaded = useContext(MapLoadedContext);
  const [features, setFeatures] = useState<maplibregl.MapboxGeoJSONFeature[]>(
    []
  );

  const mapPopup = useMemo(() => {
    const popup = new maplibregl.Popup({
      closeButton: false,
    });
    popup.setMaxWidth("500px");

    return popup;
  }, []);
  const closePopup = useCallback(() => {
    if (mapPopup.isOpen()) {
      mapPopup.remove();
    }
  }, [mapPopup]);
  const [popupLngLat, setPopupLngLat] = useState<maplibregl.LngLat | null>(
    null
  );
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!popupRef.current || !map || !popupLngLat || features.length === 0) {
      closePopup();

      return;
    }
    mapPopup.setLngLat(popupLngLat).setDOMContent(popupRef.current).addTo(map);
  }, [map, closePopup, features, popupLngLat, popupRef, mapPopup]);

  useEffect(() => {
    if (!mapLoaded || !map) {
      return;
    }
    const clickHandler = (
      e: maplibregl.MapMouseEvent & maplibregl.EventData
    ) => {
      const allFeatures = map.queryRenderedFeatures(e.point);
      const features = allFeatures.filter(
        (feature) => layerNames.indexOf(feature.sourceLayer) !== -1
      );
      setFeatures(features);
      setPopupLngLat(e.lngLat);
      if (features.length === 0) {
        return;
      }
    };

    const mousemoveHandler = (
      e: maplibregl.MapMouseEvent & maplibregl.EventData
    ) => {
      const allFeatures = map.queryRenderedFeatures(e.point);
      const features = allFeatures.filter(
        (feature) => layerNames.indexOf(feature.sourceLayer) !== -1
      );
      if (features.length === 0 && map.getCanvas().style.cursor !== "") {
        map.getCanvas().style.cursor = "";
        return;
      }

      if (features.length > 0 && map.getCanvas().style.cursor !== "pointer") {
        map.getCanvas().style.cursor = "pointer";
      }
    };

    map.on("click", clickHandler);
    map.on("mousemove", mousemoveHandler);
    // Remove event listener when we're done

    return () => {
      map.off("click", clickHandler);
      map.off("mousemove", mousemoveHandler);
    };
  }, [map, mapLoaded, layerNames]);

  return (
    <div style={{ display: "none" }}>
      <div ref={popupRef}>
        <MapPopupContent
          features={features}
          onClose={closePopup}
          renderTitle={renderTitle}
          renderSubtitle={renderSubtitle}
        />
      </div>
    </div>
  );
};

export default MapPopup;
